import type { Element, Point, Project, Wire, WireEnd } from '@elec/domain';
import { tr } from '@elec/domain';
import { getSymbol, portPosition, type SymbolDef } from '@elec/symbols';

export type SymbolLookup = (id: string) => SymbolDef | undefined;

export function wireEndPoint(
  project: Project,
  end: WireEnd,
  symbols: SymbolLookup = getSymbol,
): Point | undefined {
  if (end.kind === 'point') return { x: end.x, y: end.y };
  const el: Element | undefined = project.elements[end.elementId];
  if (!el) return undefined;
  const def = symbols(el.symbolId);
  const port = def?.ports.find((p) => p.id === end.portId);
  if (!def || !port) return { x: el.x, y: el.y };
  return portPosition(def, el, port);
}

/** Full polyline of a wire: from-end, waypoints, to-end. */
export function wirePolyline(
  project: Project,
  wire: Wire,
  symbols: SymbolLookup = getSymbol,
): Point[] | undefined {
  const a = wireEndPoint(project, wire.from, symbols);
  const b = wireEndPoint(project, wire.to, symbols);
  if (!a || !b) return undefined;
  return [a, ...wire.via, b];
}

export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

const eps = 1e-3;
const junctionCache = new WeakMap<Project, Map<string, Point[]>>();

/**
 * Connection dots: where three or more conductors meet — several wire ends at one point
 * (a port counts as one conductor), or a wire end landing on another wire's run (T-junction).
 */
export function junctionPoints(
  project: Project,
  sheetId: string,
  symbols: SymbolLookup = getSymbol,
): Point[] {
  let perSheet = junctionCache.get(project);
  if (!perSheet) junctionCache.set(project, (perSheet = new Map()));
  const cached = perSheet.get(sheetId);
  if (cached) return cached;

  const CELL = 10;
  const ends = new Map<string, { p: Point; n: number; port: boolean }>();
  const segs = new Map<string, Array<{ a: Point; b: Point; wire: string }>>();
  const key = (p: Point) => `${Math.round(p.x * 100)},${Math.round(p.y * 100)}`;
  const polylines: Array<{ id: string; pts: Point[]; ends: [Wire['from'], Wire['to']] }> = [];
  for (const w of Object.values(project.wires)) {
    if (w.sheetId !== sheetId) continue;
    const pts = wirePolyline(project, w, symbols);
    if (!pts || pts.length < 2) continue;
    polylines.push({ id: w.id, pts, ends: [w.from, w.to] });
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const x0 = Math.floor(Math.min(a.x, b.x) / CELL);
      const x1 = Math.floor(Math.max(a.x, b.x) / CELL);
      const y0 = Math.floor(Math.min(a.y, b.y) / CELL);
      const y1 = Math.floor(Math.max(a.y, b.y) / CELL);
      for (let cx = x0; cx <= x1; cx++)
        for (let cy = y0; cy <= y1; cy++) {
          const k = `${cx},${cy}`;
          let list = segs.get(k);
          if (!list) segs.set(k, (list = []));
          list.push({ a, b, wire: w.id });
        }
    }
  }
  const out: Point[] = [];
  for (const pl of polylines) {
    const endPts = [pl.pts[0]!, pl.pts.at(-1)!];
    endPts.forEach((p, i) => {
      const k = key(p);
      const e = ends.get(k) ?? { p, n: 0, port: false };
      e.n++;
      if (pl.ends[i]!.kind === 'port') e.port = true;
      ends.set(k, e);
    });
  }
  const tees = new Set<string>();
  for (const pl of polylines) {
    for (const p of [pl.pts[0]!, pl.pts.at(-1)!]) {
      const list = segs.get(`${Math.floor(p.x / CELL)},${Math.floor(p.y / CELL)}`) ?? [];
      for (const s of list) {
        if (s.wire === pl.id) continue;
        const atEnd =
          (Math.abs(s.a.x - p.x) < eps && Math.abs(s.a.y - p.y) < eps) ||
          (Math.abs(s.b.x - p.x) < eps && Math.abs(s.b.y - p.y) < eps);
        if (atEnd) {
          // A wire end on an interior corner of another wire is also a T.
          const other = polylines.find((q) => q.id === s.wire)!;
          const first = other.pts[0]!;
          const last = other.pts.at(-1)!;
          const isOtherEnd =
            (Math.abs(first.x - p.x) < eps && Math.abs(first.y - p.y) < eps) ||
            (Math.abs(last.x - p.x) < eps && Math.abs(last.y - p.y) < eps);
          if (!isOtherEnd) tees.add(key(p));
          continue;
        }
        if (distanceToSegment(p, s.a, s.b) < eps) tees.add(key(p));
      }
    }
  }
  for (const [k, e] of ends) {
    if (e.n + (e.port ? 1 : 0) >= 3 || tees.has(k)) out.push(e.p);
  }
  perSheet.set(sheetId, out);
  return out;
}

/** Extra label lines for a protective device linked to a circuit: "A · Prises séjour". */
export function circuitLabel(project: Project, el: { circuitId?: string }): string[] {
  const c = el.circuitId ? project.circuits[el.circuitId] : undefined;
  if (!c) return [];
  const head = c.label ? `${c.code} · ${c.label}` : c.code;
  // "Partie ancienne": circuits of the installation older than 1 October 1981.
  return c.legacy
    ? [
        head,
        tr(
          project.meta.locale,
          'Partie ancienne (PA)',
          'Legacy part (PA)',
          'Oud gedeelte (OG)',
          'Altanlage (AA)',
        ),
      ]
    : [head];
}
